import React, { useContext, useState } from 'react';
import { Table, UncontrolledTooltip } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import {
  ConstructionEquipment,
  FuelDataRow,
  VehicleDataRow,
} from '../../../../../../services/types/Calculator';
import { Button, ConfirmModal, DropDown, Modal } from '../../../../../../shared/components';
import { handleKeyDown } from '../../Utils/KeyboardHandlers';
import BasicRadioButton from '../../../../../../shared/components/BasicRadioButton';
import CalculatorSectorContext from '../UrbanInfrastructureTool/CalculatorSectorContext';
import MainContext from '../../../../../App/MainProvider/context';
import { calculateFuelData, calculateVehicleData } from '../../Utils/moduleCalculates';
import ConstructionEquipmentDetails from './Details/ConstructionEquipmentDetails';
import NumberFormatterSinSeparador from '../../NumberFormatterSinSeparador';
import { tooltipPreventOverflowModifiers } from '../../../../../../shared/misc/utils';

interface Props {
  constructionEquipment: ConstructionEquipment;
  onUpdateConstructionEquipment: (updatedConstructionEquipment: ConstructionEquipment) => void;
}

const ConstructionEquipmentSection: React.FC<Props> = ({
  constructionEquipment,
  onUpdateConstructionEquipment,
}) => {
  const { t } = useTranslation();
  const { notify } = useContext(MainContext);

  const [fuelDataSelectedOption, setFuelDataSelectedOption] = useState<number | null>(0);
  const [vehicleSelectedOption, setVehicleSelectedOption] = useState<number | null>(0);
  const [fuelInputValue, setFuelInputValue] = useState<string>('');
  const [vehicleInputValue, setVehicleInputValue] = useState<string>('');
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [totalCarbonSum, setTotalCarbonSum] = useState<number | null>(
    constructionEquipment.subTotal
  );
  const [fuelRows, setFuelRows] = useState<FuelDataRow[]>(constructionEquipment.fuelDataRows || []);
  const [vehicleRows, setVehicleRows] = useState<VehicleDataRow[]>(
    constructionEquipment.vehicleDataRows || []
  );
  const initialRadio =
    constructionEquipment.isFuelData !== null ? Number(constructionEquipment.isFuelData) : null;
  const [radioSelected, setRadioSelected] = useState<number | null>(initialRadio);
  const { measurementSystem } = useContext(CalculatorSectorContext);
  const [showConfirmDelete, setShowConfirmDelete] = useState<boolean>(false);
  const [rowForDelete, setRowForDelete] = useState<number | null>(null);

  const isAddButtonFuelDisabled =
    fuelDataSelectedOption === null || fuelDataSelectedOption === 0 || Number(fuelInputValue) <= 0;

  const isAddButtonVehicleDisabled =
    vehicleSelectedOption === null || vehicleSelectedOption === 0 || Number(vehicleInputValue) <= 0;

  const [expandedRowIndex, setExpandedRowIndex] = useState<number | null>(initialRadio);

  const fuelFactorsOptions = [
    { id: 0, text: t('Select'), value: '' },
    ...(constructionEquipment.fuelDataFactors
      ? constructionEquipment.fuelDataFactors.map((x) => ({
          id: x.id,
          text: x.fuelType,
          value: x.id.toString(),
        }))
      : []),
  ];

  const vehicleFactorsOptions = [
    { id: 0, text: t('Select'), value: '' },
    ...(constructionEquipment.vehicleFactors
      ? constructionEquipment.vehicleFactors.map((x) => ({
          id: x.id,
          text: x.vehicleType,
          value: x.id.toString(),
        }))
      : []),
  ];

  const handleRadioChange = (value: number) => {
    setRadioSelected(value);

    onUpdateConstructionEquipment({
      ...constructionEquipment,
      isFuelData: value === 1,
      moduleId: constructionEquipment.moduleId,
      co2Total: value === 1 ? null : constructionEquipment.co2Total,
      ch4Total: value === 1 ? null : constructionEquipment.ch4Total,
      n2OTotal: value === 1 ? null : constructionEquipment.n2OTotal,
      fuelDataRows: [],
      vehicleDataRows: [],
      subTotal: null,
    });

    setFuelRows([]);
    setVehicleRows([]);
    setTotalCarbonSum(null);

    setExpandedRowIndex(value);
  };

  const getTextMeasureQuantity = (): string =>
    measurementSystem.id === 1 ? '(Liters)' : '(Gallons)';

  const getTextMeasureDistance = (): string =>
    measurementSystem.id === 1 ? '(Kilometers)' : '(Miles)';
  const handleChangeFuel = (value: number) => {
    setFuelDataSelectedOption(value);
  };

  const handleChangeVehicle = (value: number) => {
    setVehicleSelectedOption(value);
  };

  const handleFuelInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    if (value.length > 10) {
      return;
    }

    if (value === '' || /^[0-9]*\.?[0-9]*$/.test(value)) {
      setFuelInputValue(value);
    }
  };

  const handleVehicleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    if (value.length > 10) {
      return;
    }

    if (value === '' || /^[0-9]*\.?[0-9]*$/.test(value)) {
      setVehicleInputValue(value);
    }
  };

  const isDuplicateFuelType = (rows: FuelDataRow[], coverId: number) => {
    const isDuplicate = rows.some((row) => row.fuelTypeId === coverId);

    if (isDuplicate) {
      notify(
        'Construction Equipment',
        'A row with this Fuel Type already exists.',
        'danger',
        'exclamation-triangle'
      );
    }

    return isDuplicate;
  };

  const isDuplicateVehicleType = (rows: VehicleDataRow[], coverId: number) => {
    const isDuplicate = rows.some((row) => row.vehicleTypeId === coverId);

    if (isDuplicate) {
      notify(
        'Construction Equipment',
        'A row with this Vehicle Type already exists.',
        'danger',
        'exclamation-triangle'
      );
    }

    return isDuplicate;
  };

  const handleAddFuelRow = () => {
    if (fuelDataSelectedOption !== null && Number(fuelInputValue) > 0) {
      const selectedFuelType = constructionEquipment.fuelDataFactors.find(
        (x) => x.id === fuelDataSelectedOption
      );

      if (selectedFuelType) {
        if (isDuplicateFuelType(fuelRows, selectedFuelType.id)) {
          return;
        }

        const resultValue = calculateFuelData(
          selectedFuelType.factor,
          Number(fuelInputValue),
          measurementSystem.id
        );

        const newRow: FuelDataRow = {
          id: 0,
          fuelTypeId: selectedFuelType.id,
          fuelType: selectedFuelType.fuelType,
          value: Number(fuelInputValue),
          factor: selectedFuelType.factor,
          totalEmission: resultValue,
        };

        setFuelRows((prevRows) => {
          const updatedRows = [...prevRows, newRow];
          const fuelTotalCarbonSum = updatedRows.reduce((sum, row) => sum + row.totalEmission, 0);
          onUpdateConstructionEquipment({
            ...constructionEquipment,
            fuelDataRows: updatedRows,
            subTotal: fuelTotalCarbonSum,
          });
          setTotalCarbonSum(fuelTotalCarbonSum);
          return updatedRows;
        });
        setFuelInputValue('');
        setFuelDataSelectedOption(0);
      }
    }
  };

  const handleAddVehicleRow = () => {
    if (vehicleSelectedOption !== null && Number(vehicleInputValue) > 0) {
      const selectedVehicle = constructionEquipment.vehicleFactors.find(
        (x) => x.id === vehicleSelectedOption
      );

      if (selectedVehicle) {
        if (isDuplicateVehicleType(vehicleRows, selectedVehicle.id)) {
          return;
        }

        const newRow: VehicleDataRow = {
          id: 0,
          vehicleTypeId: selectedVehicle.id,
          vehicleType: selectedVehicle.vehicleType,
          value: Number(vehicleInputValue),
          co2Factor: selectedVehicle.cO2,
          co2Emission: 0,
          ch4Factor: selectedVehicle.cH4,
          n2OFactor: selectedVehicle.n20,
          totalEmission: 0,
          ch4Emission: null,
          n2OEmission: null,
        };

        const rowResult = calculateVehicleData(newRow);

        setVehicleRows((prevRows) => {
          const updatedRows = [...prevRows, rowResult];

          const vehicleTotalCarbonSum = updatedRows.reduce(
            (sum, row) => sum + row.totalEmission,
            0
          );
          const totalCo2 = updatedRows.reduce((sum, row) => sum + row.co2Emission, 0);
          const totalCH4 = updatedRows.reduce(
            (sum, row) => sum + (row.ch4Emission !== null ? row.ch4Emission : 0),
            0
          );
          const totalN2O = updatedRows.reduce(
            (sum, row) => sum + (row.n2OEmission !== null ? row.n2OEmission : 0),
            0
          );

          onUpdateConstructionEquipment({
            ...constructionEquipment,
            vehicleDataRows: updatedRows,
            co2Total: totalCo2,
            ch4Total: totalCH4,
            n2OTotal: totalN2O,
            subTotal: vehicleTotalCarbonSum,
          });
          setTotalCarbonSum(vehicleTotalCarbonSum);
          return updatedRows;
        });
        setVehicleInputValue('');
        setVehicleSelectedOption(0);
      }
    }
  };

  const confirmRemoveFuelRow = () => {
    if (rowForDelete !== null) {
      setFuelRows((prevRows) => {
        const updatedRows = prevRows.filter((_, i) => i !== rowForDelete);
        const fuelTotalCarbonSum = updatedRows.reduce((sum, row) => sum + row.totalEmission, 0);

        setTotalCarbonSum(updatedRows.length > 0 ? fuelTotalCarbonSum : null);

        onUpdateConstructionEquipment({
          ...constructionEquipment,
          fuelDataRows: updatedRows,
          co2Total: null,
          ch4Total: null,
          n2OTotal: null,
          subTotal: updatedRows.length > 0 ? fuelTotalCarbonSum : null,
        });
        return updatedRows;
      });
      setShowConfirmDelete(false);
      setRowForDelete(null);
    }
  };

  const confirmRemoveVehicleRow = () => {
    if (rowForDelete !== null) {
      setVehicleRows((prevRows) => {
        const updatedRows = prevRows.filter((_, i) => i !== rowForDelete);
        const vehicleTotalCarbonSum = updatedRows.reduce((sum, row) => sum + row.totalEmission, 0);

        setTotalCarbonSum(updatedRows.length > 0 ? vehicleTotalCarbonSum : null);

        onUpdateConstructionEquipment({
          ...constructionEquipment,
          vehicleDataRows: updatedRows,
          subTotal: updatedRows.length > 0 ? vehicleTotalCarbonSum : null,
        });
        return updatedRows;
      });
      setShowConfirmDelete(false);
      setRowForDelete(null);
    }
  };

  const handleRemoveFuelRow = (index: number) => {
    setRowForDelete(index);
    setShowConfirmDelete(true);
  };

  return (
    <>
      <div className="row calculator-title-row">
        <div className="radio-calculator">
          <BasicRadioButton
            id="rb_counstruction_fuel"
            name="roadspaved"
            label="Enter the total amount of fuel consumed for each specified fuel type"
            radioValue={1}
            active={radioSelected === 1}
            onChange={(value) => handleRadioChange(value)}
          />
          <BasicRadioButton
            id="rb_counstruction_vehicle"
            name="roadspaved"
            label="Vehicle Traveled Data"
            radioValue={0}
            active={radioSelected === 0}
            onChange={(value) => handleRadioChange(value)}
          />
        </div>
      </div>
      {expandedRowIndex === 0 && (
        <>
          <h4 className="table-calculator-title">{t('Select Parameters')}</h4>
          <div className="form-row align-items-center calculator-forms table-wrapper-calc">
            <div className="col-sm-2 d-flex align-items-center">
              <label className="col-form-label mr-2" htmlFor="sector">
                {t('Vehicle Type')}
              </label>
            </div>
            <div className="col-sm-3 d-flex align-items-center">
              <DropDown
                elements={vehicleFactorsOptions}
                value={vehicleSelectedOption}
                onChange={handleChangeVehicle}
                key="drop-vehicleFactors"
                className="dropdown-module-calculator"
              />
            </div>
            <div className="col-sm-2 d-flex align-items-center">
              <label className="col-form-label mr-2 mr-override" htmlFor="sector">
                {t('Distance Traveled ') + getTextMeasureDistance()}
              </label>
            </div>

            <div className="col-sm-2 d-flex align-items-center">
              <input
                title="Distance Traveled"
                type="number"
                id="vehicleInput"
                value={vehicleInputValue}
                onChange={handleVehicleInputChange}
                min="0"
                max={99999999.99}
                maxLength={10}
                onKeyDown={handleKeyDown}
                className="calculator-input"
              />
            </div>
            <div className="col-sm-3">
              <Button
                htmlId="addRowVehicle"
                className="btn btn-primary px-4 py-1 calculator-add-button"
                style={{ float: 'right' }}
                text={t('Add Row')}
                onClick={handleAddVehicleRow}
                disabled={isAddButtonVehicleDisabled}
              />
            </div>
          </div>
        </>
      )}

      {expandedRowIndex === 1 && (
        <>
          <h4 className="table-calculator-title">{t('Select Parameters')}</h4>
          <div className="form-row align-items-center calculator-forms table-wrapper-calc">
            <div className="col-sm-2 d-flex align-items-center">
              <label className="col-form-label mr-2" htmlFor="sector">
                {t('Transportation Fuel')}
              </label>
            </div>
            <div className="col-sm-3 d-flex align-items-center">
              <DropDown
                elements={fuelFactorsOptions}
                value={fuelDataSelectedOption}
                onChange={handleChangeFuel}
                key="drop-fuelFactors"
                className="dropdown-module-calculator"
              />
            </div>
            <div className="col-sm-2 d-flex align-items-center">
              <label className="col-form-label mr-2 mr-override" htmlFor="sector">
                {t('Quantity of Fuel Used ') + getTextMeasureQuantity()}
              </label>
            </div>

            <div className="col-sm-2 d-flex align-items-center">
              <input
                title="Quantity of Fuel Used"
                type="number"
                id="fuelInput"
                value={fuelInputValue}
                onChange={handleFuelInputChange}
                min="0"
                max={99999999.99}
                maxLength={10}
                onKeyDown={handleKeyDown}
                className="calculator-input"
              />
            </div>
            <div className="col-sm-3">
              <Button
                htmlId="addRowFuel"
                className="btn btn-primary px-4 py-1 calculator-add-button"
                style={{ float: 'right' }}
                text={t('Add Row')}
                onClick={handleAddFuelRow}
                disabled={isAddButtonFuelDisabled}
              />
            </div>
          </div>
        </>
      )}

      <div className="table-wrapper-calc-grid">
        <Table responsive className="table--bordered  action-padding-calculator">
          <colgroup>
            <col style={{ width: '40%' }} />
            <col style={{ width: '25%' }} />
            <col style={{ width: '25%' }} />
            <col style={{ width: '10%' }} />
          </colgroup>

          {constructionEquipment.isFuelData ? (
            <thead>
              <tr>
                <th>Transportation Fuel</th>
                <th>{t('Quantity of Fuel Used ') + getTextMeasureQuantity()}</th>
                <th className="text-right">Subtotal</th>
                <th className="action-column  text-right">{t('actions')}</th>
              </tr>
            </thead>
          ) : (
            <thead>
              <tr>
                <th>Vehicle Type</th>
                <th>{t('Distance Traveled ') + getTextMeasureDistance()}</th>
                <th className="text-right">Subtotal</th>
                <th className="action-column  text-right">{t('actions')}</th>
              </tr>
            </thead>
          )}
          <tbody>
            {constructionEquipment.isFuelData &&
              constructionEquipment.fuelDataRows &&
              constructionEquipment.fuelDataRows.length > 0 &&
              fuelRows.map((row, index) => (
                <tr key={row.id}>
                  <td>{row.fuelType}</td>
                  <td>{row.value}</td>
                  <td className="text-right">
                    <NumberFormatterSinSeparador value={row.totalEmission} /> t CO<sub>2</sub>
                  </td>
                  <td className="action-column text-right">
                    <Button
                      htmlId={`remove-row-fuel-${index}`}
                      className="btn-tertiary no-padding  action-calculator"
                      icon="trash-alt"
                      text=""
                      onClick={() => handleRemoveFuelRow(index)}
                    />
                  </td>
                </tr>
              ))}
            {!constructionEquipment.isFuelData &&
              constructionEquipment.vehicleDataRows &&
              constructionEquipment.vehicleDataRows.length > 0 &&
              vehicleRows.map((row, index) => (
                <tr key={row.id}>
                  <td>{row.vehicleType}</td>
                  <td>{row.value}</td>
                  <td className="text-right">
                    <NumberFormatterSinSeparador value={row.totalEmission} /> t CO<sub>2</sub>
                  </td>
                  <td className="action-column text-right">
                    <Button
                      htmlId={`remove-row-fuel-${index}`}
                      className="btn-tertiary no-padding  action-calculator"
                      icon="trash-alt"
                      text=""
                      onClick={() => handleRemoveFuelRow(index)}
                    />
                  </td>
                </tr>
              ))}
          </tbody>
          {totalCarbonSum !== null && (
            <tfoot>
              <tr>
                <td className="subtotal-column-calculator" colSpan={3}>
                  <div>
                    Subtotal: <NumberFormatterSinSeparador value={totalCarbonSum} /> t CO
                    <sub>2</sub>
                  </div>
                </td>
                <td className="action-column text-right">
                  <Button
                    htmlId="tr-street-detail"
                    className="btn-tertiary no-padding  action-calculator"
                    icon="clipboard-list"
                    text=""
                    onClick={() => setShowDetailsModal(true)}
                  />
                  <UncontrolledTooltip
                    className="tooltip-toolkit"
                    placement="bottom"
                    target="tr-street-detail"
                    modifiers={tooltipPreventOverflowModifiers}
                  >
                    {t('Details')}
                  </UncontrolledTooltip>
                </td>
              </tr>
            </tfoot>
          )}
        </Table>
      </div>
      {showConfirmDelete && rowForDelete !== null && (
        <ConfirmModal
          visible={showConfirmDelete}
          buttonText={t('Confirm')}
          setVisible={setShowConfirmDelete}
          action={constructionEquipment.isFuelData ? confirmRemoveFuelRow : confirmRemoveVehicleRow}
        >
          {t('confirmDeleteCalculateMessage')}
        </ConfirmModal>
      )}
      <Modal
        title={constructionEquipment.name}
        visible={showDetailsModal}
        setVisible={() => setShowDetailsModal(false)}
        actions={[{ text: 'Close', action: () => setShowDetailsModal(false) }]}
        customClass="extra_wide-modal"
        onClosed={() => setShowDetailsModal(false)}
      >
        <div className="mb-0 table-responsive">
          <ConstructionEquipmentDetails constructionEquipment={constructionEquipment} />
        </div>
      </Modal>
    </>
  );
};

export default ConstructionEquipmentSection;
